import React, { useState, useRef, useEffect } from 'react';
import {
  ArrowLeft,
  ThumbsUp,
  ThumbsDown,
  MoreVertical,
  Copy,
  MessageSquare,
  Edit3,
  Trash2,
} from 'lucide-react';
import {
  JiraIcon,
  ConfluenceIcon,
  GitHubIcon,
  AzureDevOpsIcon,
  SlackIcon,
  TeamsIcon,
} from '../../common/BrandIcons';
import { Button, Avatar, Badge } from '../../ui';
import type { Assistant } from '../../../types';
import { sanitizeBrandText } from '../../../utils/helpers';
import styles from './AssistantDetails.module.css';

export interface AssistantDetailsProps {
  assistant: Assistant;
  onBack: () => void;
  onChatNow: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onLike: () => void;
  onDislike: () => void;
  onCopyId: () => void;
  onCopyInstructions: () => void;
  onCopyDetailLink: () => void;
  onCopyChatLink: () => void;
}

export const AssistantDetails: React.FC<AssistantDetailsProps> = ({
  assistant,
  onBack,
  onChatNow,
  onEdit,
  onDelete,
  onLike,
  onDislike,
  onCopyId,
  onCopyInstructions,
  onCopyDetailLink,
  onCopyChatLink,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const formatNumber = (num: number): string => {
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}k`;
    }
    return num.toString();
  };

  const handleMenuAction = (action: () => void) => {
    setShowMenu(false);
    action();
  };

  return (
    <div className={styles.container}>
      <div className={styles.mainContent}>
        {/* Header with back button */}
        <div className={styles.header}>
          <button className={styles.backButton} onClick={onBack}>
            <ArrowLeft size={20} />
          </button>
          <h1 className={styles.pageTitle}>Assistant Details</h1>
        </div>

        {/* Assistant Info */}
        <div className={styles.assistantHeader}>
          <Avatar src={assistant.avatar} name={assistant.name} size="xl" />
          <div className={styles.assistantInfo}>
            <h2 className={styles.assistantName}>{assistant.name}</h2>
            <p className={styles.assistantMeta}>
              by {sanitizeBrandText(assistant.author)} | <span className={styles.usageIcon}>📊</span>{' '}
              {formatNumber(assistant.totalUses || 0)} total uses
            </p>
          </div>
          <div className={styles.headerActions}>
            <Button
              variant="primary"
              leftIcon={<MessageSquare size={16} />}
              onClick={onChatNow}
            >
              Chat Now
            </Button>
            <div className={styles.feedbackButtons}>
              <button className={styles.feedbackButton} onClick={onLike} title="Like">
                <ThumbsUp size={18} />
              </button>
              <button className={styles.feedbackButton} onClick={onDislike} title="Dislike">
                <ThumbsDown size={18} />
              </button>
            </div>
            {/* More Options Menu */}
            <div className={styles.menuContainer} ref={menuRef}>
              <button 
                className={`${styles.menuButton} ${showMenu ? styles.active : ''}`} 
                title="More options"
                onClick={() => setShowMenu(!showMenu)}
              >
                <MoreVertical size={18} />
              </button>
              {showMenu && (
                <div className={styles.dropdownMenu}>
                  {onEdit && (
                    <button 
                      className={styles.menuItem}
                      onClick={() => handleMenuAction(onEdit)}
                    >
                      <Edit3 size={16} />
                      <span>Edit Assistant</span>
                    </button>
                  )}
                  <button 
                    className={styles.menuItem}
                    onClick={() => handleMenuAction(onCopyId)}
                  >
                    <Copy size={16} />
                    <span>Copy ID</span>
                  </button>
                  {onDelete && (
                    <>
                      <div className={styles.menuDivider} />
                      <button 
                        className={`${styles.menuItem} ${styles.danger}`}
                        onClick={() => handleMenuAction(onDelete)}
                      >
                        <Trash2 size={16} />
                        <span>Delete Assistant</span>
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* About Section */}
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>About Assistant:</h3>
          <p className={styles.description}>{assistant.description}</p>
        </section>

        {/* Conversation Starters */}
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Conversation Starters:</h3>
          <div className={styles.starters}>
            {assistant.conversationStarters.map((starter, index) => (
              <button key={index} className={styles.starterButton}>
                {starter}
              </button>
            ))}
          </div>
        </section>

        {/* System Instructions */}
        <section className={styles.section}>
          <div className={styles.instructionsHeader}>
            <h3 className={styles.sectionTitle}>System Instructions</h3>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Copy size={14} />}
              onClick={onCopyInstructions}
            >
              Copy
            </Button>
          </div>
          <div className={styles.instructionsBox}>
            <pre className={styles.instructions}>{assistant.systemInstructions}</pre>
          </div>
        </section>
      </div>

      {/* Right Sidebar */}
      <aside className={styles.sidebar}>
        <section className={styles.sidebarSection}>
          <h3 className={styles.sidebarTitle}>OVERVIEW</h3>
          <div className={styles.overviewItem}>
            <span className={styles.overviewLabel}>Project:</span>
            <span className={styles.overviewValue}>{assistant.project}</span>
          </div>
          <div className={styles.overviewItem}>
            <span className={styles.overviewLabel}>Shared status:</span>
            <span className={styles.overviewValue}>{assistant.sharedStatus}</span>
          </div>
        </section>

        <section className={styles.sidebarSection}>
          <h3 className={styles.sidebarTitle}>ASSISTANT ID:</h3>
          <div className={styles.copyField}>
            <span className={styles.copyFieldValue}>{assistant.id}</span>
            <button className={styles.copyButton} onClick={onCopyId} title="Copy ID">
              <Copy size={14} />
            </button>
          </div>
        </section>

        <section className={styles.sidebarSection}>
          <h3 className={styles.sidebarTitle}>ACCESS LINKS</h3>
          <div className={styles.linkItem}>
            <span className={styles.linkLabel}>Link to assistant details</span>
            <div className={styles.copyField}>
              <span className={styles.copyFieldValue}>
                {sanitizeBrandText(assistant.accessLinks?.details || `${window.location.origin}/assistants/${assistant.id}`)}
              </span>
              <button className={styles.copyButton} onClick={onCopyDetailLink} title="Copy link">
                <Copy size={14} />
              </button>
            </div>
          </div>
          <div className={styles.linkItem}>
            <span className={styles.linkLabel}>Link to start a chat</span>
            <div className={styles.copyField}>
              <span className={styles.copyFieldValue}>
                {sanitizeBrandText(assistant.accessLinks?.chat || `${window.location.origin}/chat/${assistant.id}`)}
              </span>
              <button className={styles.copyButton} onClick={onCopyChatLink} title="Copy link">
                <Copy size={14} />
              </button>
            </div>
          </div>
        </section>

        <section className={styles.sidebarSection}>
          <h3 className={styles.sidebarTitle}>CATEGORIES</h3>
          <div className={styles.categories}>
            {assistant.categories.map((category, index) => (
              <Badge key={index} variant="default">
                {category}
              </Badge>
            ))}
          </div>
        </section>

        <section className={styles.sidebarSection}>
          <h3 className={styles.sidebarTitle}>CONFIGURATION</h3>
          <div className={styles.configItem}>
            <span className={styles.configLabel}>LLM model:</span>
            <div className={styles.configValue}>{assistant.llmModel}</div>
          </div>
        </section>

        {/* Integrations */}
        {assistant.integrationIds && assistant.integrationIds.length > 0 && (
          <section className={styles.sidebarSection}>
            <h3 className={styles.sidebarTitle}>INTEGRATIONS</h3>
            <div className={styles.categories}>
              {assistant.integrationIds.map((integration, index) => (
                <Badge key={index} variant="default">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {integration === 'jira' && <JiraIcon size={16} />}
                    {integration === 'confluence' && <ConfluenceIcon size={16} />}
                    {integration === 'github' && <GitHubIcon size={16} />}
                    {integration === 'slack' && <SlackIcon size={16} />}
                    {integration === 'azure-devops' && <AzureDevOpsIcon size={16} />}
                    {integration === 'teams' && <TeamsIcon size={16} />}
                    <span>{integration}</span>
                  </div>
                </Badge>
              ))}
            </div>
          </section>
        )}

        {/* Datasources */}
        {assistant.datasourceIds && assistant.datasourceIds.length > 0 && (
          <section className={styles.sidebarSection}>
            <h3 className={styles.sidebarTitle}>DATASOURCES</h3>
            <div className={styles.categories}>
              {assistant.datasourceIds.map((dsId, index) => (
                <Badge key={index} variant="default">
                  📚 {dsId.slice(0, 8)}...
                </Badge>
              ))}
            </div>
          </section>
        )}
      </aside>
    </div>
  );
};

export default AssistantDetails;
